import React from 'react';
import { motion } from 'framer-motion';
import { FiLock, FiZap } from 'react-icons/fi';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { FeatureFlags, getUpgradeMessage } from '@/services/featureFlagsService';

interface FeatureGateProps {
  feature: keyof FeatureFlags;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
}

/**
 * Component that conditionally renders children based on feature flags
 */
export function FeatureGate({ 
  feature, 
  children, 
  fallback,
  showUpgradePrompt = true 
}: FeatureGateProps) {
  const { flags } = useFeatureFlags();
  
  const featureValue = flags[feature];
  const isEnabled = typeof featureValue === 'boolean' ? featureValue : featureValue !== null && featureValue !== 'none';
  
  if (isEnabled) {
    return <>{children}</>;
  }
  
  if (fallback) {
    return <>{fallback}</>;
  }
  
  if (showUpgradePrompt) {
    return <UpgradePrompt feature={feature} />;
  }
  
  return null;
}

interface UpgradePromptProps {
  feature: keyof FeatureFlags;
  compact?: boolean;
}

export function UpgradePrompt({ feature, compact = false }: UpgradePromptProps) {
  const message = getUpgradeMessage(feature);
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <FiLock className="text-purple-400" />
        <span>{message}</span>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/30 text-center"
    >
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
        <FiLock className="text-purple-400 text-xl" />
      </div>
      <h3 className="text-white font-semibold mb-2">Feature Locked</h3>
      <p className="text-slate-400 text-sm mb-4">{message}</p>
      <a
        href="/pricing"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:from-purple-500 hover:to-cyan-500 transition-all"
      >
        <FiZap />
        Upgrade Now
      </a>
    </motion.div>
  );
}

interface AnalysisLimitGateProps {
  children: React.ReactNode;
  onLimitReached?: () => void;
}

/**
 * Gate specifically for AI analysis with limit checking
 */
export function AnalysisLimitGate({ children, onLimitReached }: AnalysisLimitGateProps) {
  const { canAnalyze, analysisCheck, analysesRemaining, planInfo } = useFeatureFlags();
  
  if (!canAnalyze) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/30 text-center"
      >
        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
          <FiLock className="text-purple-400 text-xl" />
        </div>
        <h3 className="text-white font-semibold mb-2">
          {planInfo.name === 'No Plan' ? 'Subscription Required' : 'Analysis Limit Reached'}
        </h3>
        <p className="text-slate-400 text-sm mb-4">{analysisCheck.reason}</p>
        <a
          href="/pricing"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:from-purple-500 hover:to-cyan-500 transition-all"
        >
          <FiZap />
          {planInfo.name === 'No Plan' ? 'Get Started' : 'Upgrade to Pro Trader'}
        </a>
      </motion.div>
    );
  }
  
  return <>{children}</>;
}

/**
 * Badge showing remaining analyses for Starter plan
 */
export function AnalysisLimitBadge() {
  const { flags, analysesRemaining, planInfo } = useFeatureFlags();
  
  // Only show for Starter plan with limits
  if (flags.aiAnalysesLimit === null) {
    return (
      <span className="text-xs text-slate-500">Unlimited analyses</span>
    );
  }
  
  if (!flags.canUseAiAnalysis) {
    return null;
  }
  
  const remaining = analysesRemaining ?? 0;
  const total = flags.aiAnalysesLimit;
  const isLow = remaining <= 3;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
      isLow 
        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
        : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
    }`}>
      <span>{remaining}/{total} analyses left</span>
      {isLow && <span className="text-amber-300">• Low</span>}
    </div>
  );
}

export default FeatureGate;
